import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { automatedAnswerAudit, type ActorDTO, type DecisionDTO, type DecisionKind } from "@roomate/core";
import { useActors, useAnswer, useDecision, useInbox, useMe, useProjects, useRecentAnswers } from "../api.ts";
import { skillSourceUrl } from "../lib/skills-ui.ts";
import { addLink } from "../components/skills/skills.ts";
import { Action, Avatar, Bento, Disclosure, Page, PageHeader, Pill, Tile } from "../calm.tsx";
import { ActorName, Ago, cx, ErrorNote, inputCls, Markdown } from "../ui.tsx";
import { tidyContext } from "../lib/tidy-context.ts";
import { GUARD_HASH_LABEL } from "../lib/guard.ts";
import { pickDecision } from "../lib/decision-link.ts";
import { ChiefNote } from "../components/ChiefNote.tsx";
import { DecisionCrumb, ProjectMark } from "../components/DecisionCrumb.tsx";
import { filterByProject, projectCounts, validProjectFilter } from "../lib/project-mark.ts";

export const KIND_LABEL: Record<DecisionKind, string> = {
  approve_spec: "Spec approval",
  review_change: "Review",
  question: "Question",
  exception: "Rule exception",
  risk_acceptance: "Risk acceptance",
  spot_check: "Spot-check",
  human_action: "Needs a person",
};

export const NEEDS_RATIONALE = new Set(["request_changes", "reject", "issue", "cannot"]);

const TIER_TONE = { low: "live", medium: "signal", high: "danger", critical: "danger" } as const;

/*
 * Inbox: one decision at a time, like Home. The rest of the queue sits in a quiet
 * list below it; what's already been answered folds away in a Disclosure.
 */
export function InboxPage() {
  const inbox = useInbox();
  const answered = useRecentAnswers();
  const actors = useActors().data;
  const projects = useProjects().data;
  // `?decision=` opens one decision directly (the run transcript's guard notes link here).
  const [params, setParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(params.get("decision"));
  const linkedId = params.get("decision");
  useEffect(() => {
    if (linkedId) setSelectedId(linkedId);
  }, [linkedId]);
  // `?project=` narrows the queue to one project; it survives reloads and links.
  const projectFilter = validProjectFilter(params.get("project"), projects);
  const all = inbox.data ?? [];
  const list = filterByProject(all, projectFilter);
  const setProjectFilter = (id: string | null) => {
    // The open card may belong to another project: let the new list pick its own first decision.
    setSelectedId(null);
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (id) next.set("project", id);
        else next.delete("project");
        // A reload must not bring the old card back.
        next.delete("decision");
        return next;
      },
      { replace: true },
    );
  };
  // A linked decision that is already answered is not in the open list: fetch it by id.
  const inList = !selectedId || list.some((d) => d.id === selectedId);
  const linkedQ = useDecision(!inList && selectedId ? selectedId : "");
  const { decision: selected, linkedPending } = pickDecision(list, selectedId, linkedQ.data, projectFilter);
  const rest = list.filter((d) => d.id !== selected?.id);

  // j/k to move between decisions.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) return;
      if (!list.length) return;
      // A read-only (linked, answered) decision is not in the list: j/k start from the top.
      const at = list.findIndex((d) => d.id === selected?.id);
      if (e.key === "j") setSelectedId(list[at < 0 ? 0 : Math.min(list.length - 1, at + 1)]!.id);
      if (e.key === "k") setSelectedId(list[at < 0 ? 0 : Math.max(0, at - 1)]!.id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [list, selected?.id]);

  return (
    <Page wide>
      <PageHeader
        title="Inbox"
        meta={
          inbox.isLoading ? (
            <span className="text-ink-3">Loading…</span>
          ) : list.length === 0 ? (
            <span>{all.length > 0 ? "Nothing is waiting in this project." : "Nothing is waiting on you."}</span>
          ) : (
            <span>
              <span className="font-medium text-ink">{list.length}</span> waiting · highest priority first
            </span>
          )
        }
      />

      {projects && projects.length > 0 && all.length > 0 && (
        <div role="group" aria-label="Filter by project" data-testid="inbox-project-filter" className="mt-5 flex flex-wrap gap-2">
          {projectCounts(all, projects).map((c) => (
            <button
              key={c.id ?? "all"}
              type="button"
              onClick={() => setProjectFilter(c.id)}
              aria-pressed={projectFilter === c.id}
              data-testid={`inbox-filter-${c.id ?? "all"}`}
              className={cx("inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[13px] transition-colors", projectFilter === c.id ? "bg-ink text-paper" : "bg-ink/[0.05] text-ink-2 hover:bg-ink/[0.09]")}
            >
              {c.id && <ProjectMark id={c.id} name={c.name} size={14} />}
              {c.name}
              <span className="tabular-nums opacity-70" data-testid={`inbox-filter-count-${c.id ?? "all"}`}>
                {c.count}
              </span>
            </button>
          ))}
        </div>
      )}

      <Bento className="mt-8">
        {selected ? (
          <DecisionTile key={selected.id} decision={selected} actors={actors} readOnly={selected.status !== "open"} onAnswered={() => setSelectedId(null)} />
        ) : linkedPending && linkedQ.error ? (
          <Tile span={6}>
            <ErrorNote error={linkedQ.error} />
          </Tile>
        ) : linkedPending || inbox.isLoading ? (
          <Tile span={6}>
            <p className="text-[13.5px] text-ink-3">Loading…</p>
          </Tile>
        ) : (
          <Tile span={6}>
            <p className="text-[19px] font-medium leading-snug">{all.length > 0 ? "Nothing here." : "Inbox zero."}</p>
            <p className="mt-1.5 text-[13.5px] text-ink-2">
              {all.length > 0 ? (
                <>
                  Other projects still have decisions waiting.{" "}
                  <button type="button" onClick={() => setProjectFilter(null)} className="underline decoration-rule hover:text-ink">
                    Show all
                  </button>
                </>
              ) : (
                "Agents keep working; you'll see new decisions here the moment they need you."
              )}
            </p>
          </Tile>
        )}

        {rest.length > 0 && (
          <Tile span={6} title="Also waiting" aside={rest.length}>
            <ul className="divide-y divide-rule/60" aria-label="Other decisions">
              {rest.map((d) => {
                const asker = actors?.find((a) => a.id === d.askedBy);
                return (
                  <li key={d.id}>
                    <button onClick={() => setSelectedId(d.id)} className="group flex w-full items-center gap-3 rounded-2xl py-2.5 text-left transition-colors hover:bg-ink/[0.04]">
                      <Avatar name={asker?.name ?? "?"} kind={asker?.kind ?? "agent"} size={34} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[14px] font-medium">{d.question}</span>
                        <span className="block truncate text-[12px] text-ink-3">
                          {KIND_LABEL[d.kind]} · {projects?.find((p) => p.id === d.projectId)?.name} · <Ago ts={d.createdAt} />
                        </span>
                      </span>
                      {d.blocking && d.priority.blockedMin > 0 && <span className="shrink-0 text-[12px] font-medium text-high">blocking</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          </Tile>
        )}

        <Tile span={6} title="Recently answered">
          {(answered.data ?? []).length === 0 ? (
            <p className="text-[13.5px] text-ink-3">Nothing yet.</p>
          ) : (
            <Disclosure summary={answered.data!.length > 8 ? `${answered.data!.length} answered · showing 8` : `${answered.data!.length} answered`}>
              <ul className="space-y-1.5">
                {answered.data!.slice(0, 8).map((d) => (
                  <li key={d.id} className="flex items-center gap-2 text-[13px] text-ink-2">
                    <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[color:var(--ok)] text-[11px] text-white">✓</span>
                    <span className="min-w-0 flex-1 truncate">{d.question}</span>
                    <span className="shrink-0 text-ink-3">{d.answer?.optionId}</span>
                  </li>
                ))}
              </ul>
            </Disclosure>
          )}
        </Tile>
      </Bento>
    </Page>
  );
}

/** A decision someone already answered, opened from an audit link: what was asked and what was chosen. No buttons. */
function AnsweredDecision({ decision, actors }: { decision: DecisionDTO; actors: ActorDTO[] | undefined }) {
  const guardAsk = decision.kind === "risk_acceptance" && decision.evidence.some((e) => e.label === GUARD_HASH_LABEL);
  const option = decision.options.find((o) => o.id === decision.answer?.optionId);
  const answerer = actors?.find((actor) => actor.id === decision.answer?.by);
  return (
    <Tile span={6}>
      <article aria-labelledby="decision-q" data-testid="decision-answered">
        <div className="flex flex-wrap items-center gap-2 text-[13px] text-ink-2">
          <Pill tone={TIER_TONE[decision.tier]}>{decision.tier}</Pill>
          <span>{KIND_LABEL[decision.kind]}</span>
          {guardAsk && <Pill tone="signal" testId="guard-decision-pill">Command guard</Pill>}
          <Pill tone="neutral">answered</Pill>
          {decision.taskId && (
            <Link to={`/tasks/${decision.taskId}`} className="ml-auto text-ink-2 underline decoration-rule hover:text-ink">
              Open task
            </Link>
          )}
        </div>
        <div className="mt-3">
          <DecisionCrumb decision={decision} />
        </div>
        <h2 id="decision-q" className="mt-2 text-[24px] font-semibold leading-snug tracking-[-0.02em] md:text-[28px]">
          {decision.question}
        </h2>
        {decision.contextMd && (
          <div className="mt-5">
            <FoldedContext md={decision.contextMd} />
          </div>
        )}
        {decision.answer && (
          <p className="mt-6 rounded-2xl bg-ink/[0.04] px-4 py-3 text-[14px] text-ink-2" data-testid="decision-answer">
            {decision.answer.automated ? (
              <>
                <span className="font-medium text-ink">{automatedAnswerAudit(answerer?.name ?? "someone")}</span>
                {" · "}
                <span className="font-medium text-ink">{option?.label ?? decision.answer.optionId}</span>
                {" · "}
                <Ago ts={decision.answer.at} />
              </>
            ) : (
              <>
                Answered <span className="font-medium text-ink">{option?.label ?? decision.answer.optionId}</span> by <ActorName id={decision.answer.by} actors={actors} /> · <Ago ts={decision.answer.at} />
              </>
            )}
            {decision.answer.rationale && <span className="mt-1 block text-ink-3">“{decision.answer.rationale}”</span>}
          </p>
        )}
      </article>
    </Tile>
  );
}

export function DecisionTile({
  decision,
  actors,
  readOnly = false,
  onAnswered,
}: {
  decision: DecisionDTO;
  actors: ActorDTO[] | undefined;
  readOnly?: boolean;
  onAnswered?: () => void;
}) {
  const guardAsk = decision.kind === "risk_acceptance" && decision.evidence.some((e) => e.label === GUARD_HASH_LABEL);
  const answer = useAnswer();
  const [choice, setChoice] = useState<string | null>(null);
  const [rationale, setRationale] = useState("");
  const textRef = useRef<HTMLTextAreaElement>(null);
  const asker = actors?.find((a) => a.id === decision.askedBy);
  const options = useMemo(() => decision.options, [decision.options]);

  const submit = (optionId: string) => {
    if (NEEDS_RATIONALE.has(optionId) && !rationale.trim()) {
      setChoice(optionId);
      textRef.current?.focus();
      return;
    }
    // After an answer, the next open decision comes up, not this (now answered) one.
    answer.mutate({ id: decision.id, optionId, rationale }, { onSuccess: () => onAnswered?.() });
  };

  // Number keys answer the open decision (1 = first option).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (readOnly || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) return;
      const n = Number(e.key);
      if (n >= 1 && n <= options.length) submit(options[n - 1]!.id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (readOnly) return <AnsweredDecision decision={decision} actors={actors} />;

  return (
    <Tile span={6}>
      <article aria-labelledby="decision-q">
        <div className="flex flex-wrap items-center gap-2 text-[13px] text-ink-2">
          <Pill tone={TIER_TONE[decision.tier]}>{decision.tier}</Pill>
          <span>{KIND_LABEL[decision.kind]}</span>
          {guardAsk && <Pill tone="signal" testId="guard-decision-pill">Command guard</Pill>}
          {decision.taskId && (
            <Link to={`/tasks/${decision.taskId}`} className="ml-auto text-ink-2 underline decoration-rule hover:text-ink">
              Open task
            </Link>
          )}
        </div>
        <div className="mt-3">
          <DecisionCrumb decision={decision} />
        </div>
        <h2 id="decision-q" className="mt-2 text-[24px] font-semibold leading-snug tracking-[-0.02em] md:text-[28px]">
          {decision.question}
        </h2>
        <div className="mt-3 flex items-center gap-2.5 text-[13px] text-ink-2">
          {asker && <Avatar name={asker.name} kind={asker.kind} size={24} />}
          <span>
            {asker?.name ?? "Someone"}
            {asker?.kind === "agent" && asker.ownerId && (
              <>
                {" "}
                (owner <ActorName id={asker.ownerId} actors={actors} />)
              </>
            )}{" "}
            · <Ago ts={decision.createdAt} />
          </span>
        </div>

        {decision.contextMd && (
          <div className="mt-5">
            <FoldedContext md={decision.contextMd} />
          </div>
        )}
        {decision.evidence.length > 0 && (
          <ul className="mt-4 space-y-1 text-[13px] text-ink-2">
            {decision.evidence.map((e, i) => (
              <li key={i}>
                {e.label}: <span className="text-ink-3">{e.ref}</span>
              </li>
            ))}
          </ul>
        )}

        <AddToSkillsLibrary evidence={decision.evidence} />

        <ChiefNote note={decision.chiefNote} className="mt-6" />
        <div className={cx("grid gap-2 sm:grid-cols-2", decision.chiefNote ? "mt-3" : "mt-6")}>
          {options.map((o, i) => {
            const recommended = decision.recommendation === o.id;
            return (
              <button
                key={o.id}
                onClick={() => submit(o.id)}
                disabled={answer.isPending}
                data-option={o.id}
                className={cx(
                  "group relative rounded-2xl px-4 py-3 text-left transition-[background,transform] active:scale-[0.99] disabled:opacity-50",
                  recommended ? "bg-ink text-paper hover:opacity-90" : "bg-ink/[0.05] hover:bg-ink/[0.09]",
                  choice === o.id && "ring-2 ring-ink",
                )}
              >
                <div className="flex items-baseline gap-2">
                  <span className={cx("os-kbd shrink-0", recommended && "border-transparent bg-paper/15 text-paper")}>{i + 1}</span>
                  <span className="font-medium">{o.label}</span>
                  {recommended && (
                    <span className="ml-auto text-[12px] opacity-70">
                      recommended{decision.confidence != null ? ` · ${Math.round(decision.confidence * 100)}%` : ""}
                    </span>
                  )}
                </div>
                {o.consequences && <p className={cx("mt-1 pl-7 text-[12.5px]", recommended ? "text-paper/80" : "text-ink-2")}>{o.consequences}</p>}
              </button>
            );
          })}
        </div>

        <label className="mt-4 block">
          <span className="text-[13px] text-ink-2">
            Rationale {choice && NEEDS_RATIONALE.has(choice) ? <span className="text-high">(required for “{choice.replace("_", " ")}”)</span> : <span className="text-ink-3">(optional, recorded)</span>}
          </span>
          <textarea
            ref={textRef}
            rows={2}
            className={cx(inputCls, "mt-1.5 resize-y rounded-xl")}
            placeholder="Explain why, so the next person (or agent) understands"
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
          />
        </label>
        {choice && NEEDS_RATIONALE.has(choice) && rationale.trim() && (
          <Action primary onClick={() => submit(choice)} disabled={answer.isPending}>
            Send “{choice.replace("_", " ")}”
          </Action>
        )}
        <div className="mt-3">
          <ErrorNote error={answer.error} />
        </div>
        <p className="mt-3 text-[12px] text-ink-3">keys: 1 to {options.length} answer · j/k move</p>
      </article>
    </Tile>
  );
}

/** An agent's proposed skill (evidence labelled `skill-source`): an admin opens the Skills page with the URL prefilled. Nothing is installed here. */
function AddToSkillsLibrary({ evidence }: { evidence: DecisionDTO["evidence"] }) {
  const url = skillSourceUrl(evidence);
  const me = useMe().data;
  if (!url || me?.access !== "admin") return null;
  return <AddToSkillsLink url={url} />;
}

export function AddToSkillsLink({ url }: { url: string }) {
  return (
    <div className="mt-4">
      <Link to={addLink(url)} className="inline-flex rounded-full bg-ink/[0.05] px-3.5 py-1.5 text-[13px] font-medium hover:bg-ink/[0.09]" data-testid="add-to-skills-library">
        Add to Skills library
      </Link>
    </div>
  );
}

/** A decision's context, tidied and folded to a few lines until the reader asks for more. */
function FoldedContext({ md }: { md: string }) {
  const [open, setOpen] = useState(false);
  const text = tidyContext(md);
  const long = text.length > 700;
  return (
    <div>
      <div className={cx("relative overflow-hidden", long && !open && "max-h-[14em]")}>
        <Markdown text={text} />
        {long && !open && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[color:var(--card)] to-transparent" />}
      </div>
      {long && (
        <button type="button" onClick={() => setOpen((o) => !o)} className="mt-2 text-[13px] text-ink-2 hover:text-ink">
          {open ? "Show less" : "Read more"}
        </button>
      )}
    </div>
  );
}
